import { useCallback } from 'react';

import { useAppStore, type IAppStore } from '@/store';
import useTouchpoint from '@/hooks/useTouchpoint';

import ensureSufficientCredits from './ensureSufficientCredits';
import openOfferIfInsufficient from './openOfferIfInsufficient';

const selector = (store: IAppStore) => ({
  credits: store.credits,
  isCreditsLoading: store.isCreditsLoading,
});

/**
 * The single React entry point for credits.
 *
 *   - `credits` / `isCreditsLoading`: reactive display state populated by
 *     useLibraryItemInfoMutation when a preset loads.
 *   - `requireCredits(flowId)`: the gate-time check. Returns true if the user
 *     can proceed; on insufficient credits opens the offer/upsell modal with
 *     the correct toolId for the current surface.
 */
const useCredits = () => {
  const { credits, isCreditsLoading } = useAppStore(selector);
  const { isPortfolioApp } = useTouchpoint();

  const requireCredits = useCallback(
    async (flowId: string): Promise<boolean> => {
      const result = await ensureSufficientCredits({ flowId });
      if (result.sufficient) return true;
      openOfferIfInsufficient(result, {
        preferSubscription: isPortfolioApp,
      });
      return false;
    },
    [isPortfolioApp]
  );

  return { credits, isCreditsLoading, requireCredits };
};

export default useCredits;
